import { css, cx } from "@emotion/css";
import { Avatar, Badge, ListItemAvatar } from "@mui/material";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import dayjs from "dayjs";
import type { FunctionComponent, ReactNode, SVGProps } from "react";
import { useTranslation } from "react-i18next";
import BellOffIcon from "src/images/Icons/newIcons/bellOff.svg";
import CheckAllIcon from "src/images/Icons/newIcons/checkAll.svg";
import DeleteIcon from "src/images/Icons/newIcons/delete.svg";

import { removeQueryDetailsCache } from "../../../hooks/useExam";
import {
	useRemoveNotification,
	useRemoveNotificationHard,
	useRemoveNotificationSubscription,
} from "../../../hooks/useNotifications";
import { NotificationDate } from "./Link";
import type { NotificationData } from "./List";
import { NotificationStatus } from "./List";
import { Actions } from "./Menu";

interface SecondaryActionProps {
	className?: string;
	notification: NotificationData;
	disabledActionsList?: string[];
}

export const SecondaryAction = ({
	className,
	notification,
	disabledActionsList = [],
}: SecondaryActionProps) => {
	const { t } = useTranslation();
	const { mutateAsync } = useRemoveNotification();
	const { mutateAsync: removeSubscription } =
		useRemoveNotificationSubscription();
	const { mutateAsync: removeNotification } = useRemoveNotificationHard();

	return (
		<Actions
			className={className}
			items={[
				{
					action: () => (mutateAsync as any)(notification),
					Icon: CheckAllIcon,
					text: t("notifications.actions.setAsRead"),
					disabled: notification.status !== NotificationStatus.waiting,
				},
				{
					action: () => (removeSubscription as any)(notification),
					Icon: BellOffIcon,
					text: t("notifications.actions.stopSubscribe"),
					disabled: disabledActionsList.includes("stopSubscribe"),
				},
				{
					action: () => (removeNotification as any)(notification),
					Icon: DeleteIcon,
					className: "list-item-error",
					text: t("notifications.actions.remove"),
					disabled: disabledActionsList.includes("remove"),
				} as any,
			]}
		/>
	);
};

interface NotificationItemProps {
	notification: NotificationData;
	removeNotification: () => Promise<unknown>;
	onRedirect: (notification: NotificationData) => void;
	Icon: FunctionComponent<SVGProps<SVGSVGElement>>;
	Primary: ReactNode;
	Secondary?: ReactNode;
	disabledActionsList?: string[];
	className?: string;
}

function NotificationItem({
	notification,
	removeNotification,
	onRedirect,
	Icon,
	Primary,
	Secondary,
	disabledActionsList,
}: NotificationItemProps) {
	const { t } = useTranslation();
	const redirect = async () => {
		removeQueryDetailsCache(notification.itemId);
		await removeNotification();
		onRedirect(notification);
	};

	const renderSecondary = () => {
		if (Secondary) {
			return Secondary;
		}

		return (
			<NotificationDate status={notification.status}>
				{t("notifications.createdAt", {
					date: dayjs(notification.createdAt).format("YYYY.MM.DD"),
				})}
			</NotificationDate>
		);
	};

	return (
		<ListItem
			className="flex items-start list-item px-12 py-2 notification--item"
			onClick={redirect}
		>
			<ListItemAvatar
				className={cx(css`
          min-width: unset;
        `)}
			>
				<Badge
					className="badge-left-center"
					color="secondary"
					variant="dot"
					invisible={notification.status !== NotificationStatus.waiting}
				>
					<Avatar className="icon-super-big bg-transparent">
						<Icon className="icon-small icon-black" />
					</Avatar>
				</Badge>
			</ListItemAvatar>
			<ListItemText
				disableTypography
				primary={<div className="size-14-light text-dark-grey">{Primary}</div>}
				secondary={renderSecondary()}
			/>
			<SecondaryAction
				className="p-relative top-unset right-unset transform-unset align-self-start"
				notification={notification}
				disabledActionsList={disabledActionsList}
			/>
		</ListItem>
	);
}

export const Notification = NotificationItem;
